import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCountry } from "../context/CountryContext";
import { useAuth } from "../context/AuthContext";
import { Globe, LogOut } from "lucide-react";
import { useBrand } from "../lib/brand";
import { InstallPWA } from "./InstallPWA";
import { NotificationBell } from "./NotificationBell";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";

const LANG_LABELS = { fr: "Français", en: "English", de: "Deutsch", es: "Español" };

export function AppHeader({ variant = "landing" }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { active, current, currentCountry, setCountry } = useCountry();
  const { user, logout } = useAuth();
  const { firstWord, Icon } = useBrand();

  const _langs = (typeof localStorage !== "undefined" && localStorage.getItem("bsg_zone") === "africa") ? ["fr", "en"] : ["fr", "en", "de", "es"];
  const _cur = (i18n.language || "fr").slice(0, 2);
  const lang = (_langs.includes(_cur) ? _cur : "fr").toUpperCase();

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#FDF8F0]/80 border-b border-[#E2DFD8]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <button
          data-testid="brand-logo"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 group"
        >
          <span className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#F26419] to-[#D94F2B] flex items-center justify-center">
            <Icon className="w-6 h-6 text-white" strokeWidth={2.4} />
          </span>
          <span className="font-head font-extrabold text-2xl tracking-tight text-[#1A1A1A]">
            {firstWord}<span className="text-[#F26419]"> Go</span>
          </span>
        </button>

        <div className="flex items-center gap-2 sm:gap-3">
          {/* Country selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="country-selector"
                className="flex items-center gap-1.5 h-10 px-3 rounded-xl bg-white border border-[#E2DFD8] hover:border-[#D94F2B] transition-colors text-sm font-medium"
              >
                <span className="text-base">{currentCountry?.flag || "🌍"}</span>
                <span className="hidden sm:inline">{currentCountry?.name || current}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {active.map((c) => (
                <DropdownMenuItem
                  key={c.code}
                  data-testid={`country-option-${c.code}`}
                  onClick={() => setCountry(c.code)}
                >
                  <span className="mr-2">{c.flag}</span> {c.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Language selector */}
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="lang-toggle"
                className="flex items-center gap-1.5 h-10 px-3 rounded-xl bg-white border border-[#E2DFD8] hover:border-[#D94F2B] transition-colors text-sm font-semibold"
              >
                <Globe className="w-4 h-4" />
                {lang}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {_langs.map((l) => (
                <DropdownMenuItem key={l} data-testid={`lang-option-${l}`} onClick={() => setTimeout(() => i18n.changeLanguage(l), 0)}>
                  {LANG_LABELS[l] || l.toUpperCase()}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <InstallPWA />
          {variant === "landing" && (
            <Button
              data-testid="header-team-btn"
              onClick={() => navigate("/equipe")}
              className="h-10 rounded-xl bg-[#374151] hover:bg-[#1f2937] text-white font-semibold hidden sm:flex"
            >
              {t("agent.space")}
            </Button>
          )}
          {variant !== "landing" && <NotificationBell />}

          {variant === "landing" ? (
            <Button
              data-testid="header-login-btn"
              onClick={() => navigate("/auth?role=passenger")}
              className="h-10 rounded-xl bg-[#1A1A1A] hover:bg-black text-white font-semibold hidden sm:flex"
            >
              {t("nav.login")}
            </Button>
          ) : (
            <button
              data-testid="logout-btn"
              onClick={() => { logout(); navigate("/"); }}
              className="flex items-center gap-1.5 h-10 px-3 rounded-xl bg-white border border-[#E2DFD8] hover:border-[#D32F2F] hover:text-[#D32F2F] transition-colors text-sm font-medium"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">{t("common.logout")}</span>
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
